<template>
  <!-- Ficha de Dispositivo Médico (NT 226 / NGT 247 MINSAL).
       Reutilizable: ProductDialog (nuevo) y el diálogo de edición de Inventario.
       Se muestra en instituciones de salud o si el producto ya es DM. -->
  <v-card v-if="visible" variant="outlined" class="pa-3 dm-card">
    <div class="d-flex align-center flex-wrap">
      <v-switch v-model="p.es_dispositivo_medico" label="Es dispositivo médico (NT 226)" color="teal" inset hide-details class="mr-6"></v-switch>
      <v-switch v-if="p.es_dispositivo_medico" v-model="p.es_dm_priorizado" label="DM priorizado (NGT 247)" color="teal" inset hide-details></v-switch>
      <v-spacer></v-spacer>
      <v-chip v-if="p.tipo_dm === 'implantable'" size="small" color="red" variant="tonal">Implantable: exige registro hasta el paciente</v-chip>
    </div>

    <v-row v-if="p.es_dispositivo_medico" class="mt-1">
      <v-col cols="12" md="6">
        <v-select v-model="p.tipo_dm" :items="cat.tipos_dm" item-title="label" item-value="value" label="Tipo de DM priorizado"
          :rules="p.es_dm_priorizado ? [v => !!v || 'Un DM priorizado requiere tipo'] : []" clearable
          hint="Equipo crítico, DM del sistema de acreditación o implantable del Anexo 1" persistent-hint></v-select>
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field v-model="p.numero_registro_sanitario" label="N° registro sanitario ISP" maxlength="50" hint="NGT 247 §7.1 b — cuando corresponda" persistent-hint clearable></v-text-field>
      </v-col>

      <template v-if="p.tipo_dm === 'implantable'">
        <v-col cols="12" md="6">
          <v-select v-model="p.categoria_implantable" :items="cat.anexo1" item-title="label" item-value="categoria" label="Categoría (Anexo 1 NGT 247) *"
            :rules="[v => !!v || 'Un implantable requiere categoría del Anexo 1']"></v-select>
        </v-col>
        <v-col cols="12" md="6">
          <v-combobox v-model="p.dispositivo_anexo1" :items="anexo1Dispositivos" label="Dispositivo del Anexo 1" hint="Elige de la lista o escribe uno equivalente" persistent-hint clearable></v-combobox>
        </v-col>
      </template>

      <v-col cols="12" md="4"><v-text-field v-model="p.fabricante" label="Fabricante" maxlength="150" clearable></v-text-field></v-col>
      <v-col cols="12" md="4"><v-text-field v-model="p.marca" label="Marca" maxlength="100" clearable></v-text-field></v-col>
      <v-col cols="12" md="4"><v-text-field v-model="p.modelo" label="Modelo" maxlength="100" hint="NT 226: cuando corresponda" persistent-hint clearable></v-text-field></v-col>
      <v-col cols="12" md="6">
        <v-text-field v-model="p.denominacion_interna" label="Denominación interna del establecimiento" maxlength="150" hint="NT 226 §IX.c — si difiere del nombre comercial" persistent-hint clearable></v-text-field>
      </v-col>
      <v-col cols="12" md="6">
        <v-switch v-model="p.requiere_serie" label="Exige número de serie por unidad" color="teal" inset hint="Las series pueden registrarse en diferido con clave agrupadora (NT 226)" persistent-hint></v-switch>
      </v-col>

      <v-col cols="12" md="6">
        <v-select v-model="p.politica_vencimiento" :items="cat.politicas_vencimiento" item-title="label" item-value="value" label="Vencimiento"></v-select>
      </v-col>
      <template v-if="p.politica_vencimiento === 'fecha_sustituta'">
        <v-col cols="12" md="6">
          <v-select v-model="p.criterio_fecha_sustituta" :items="cat.criterios_fecha_sustituta" item-title="label" item-value="value" label="Criterio de la fecha sustituta *"
            :rules="[v => !!v || 'La fecha sustituta exige un criterio (NT 226 §IX.c)']"></v-select>
        </v-col>
        <v-col cols="12" md="6" v-if="['recepcion','produccion'].includes(p.criterio_fecha_sustituta)">
          <v-text-field v-model.number="p.dias_fecha_sustituta" label="Días a sumar *" type="number" min="1" :rules="[v => (v && v > 0) || 'Indica los días']"></v-text-field>
        </v-col>
      </template>
      <v-col cols="12" md="6">
        <v-text-field v-model.number="p.dias_alerta_vencimiento" label="Alerta de vencimiento (días antes)" type="number" min="0" hint="Vacío = usa el valor institucional" persistent-hint clearable></v-text-field>
      </v-col>
    </v-row>
  </v-card>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import apiClient from '@/api/index.js'

import { DM_DEFAULT_FIELDS } from '@/utils/dmFields'

const props = defineProps({
  // Objeto reactivo del producto (se muta in place: v-model sobre el mismo objeto)
  modelValue: { type: Object, required: true },
  // Forzar visibilidad aunque no sea sector salud
  force: { type: Boolean, default: false }
})

const store = useStore()
const p = computed(() => props.modelValue)
const sectorSalud = computed(() => !!store.getters['edition/sectorSalud'])
const visible = computed(() => props.force || sectorSalud.value || !!p.value.es_dispositivo_medico)

// Catálogos (fuente única backend: /api/dm/catalogos)
const cat = ref({ tipos_dm: [], anexo1: [], estados_dm: [], formas_adquisicion: [], politicas_vencimiento: [], criterios_fecha_sustituta: [] })
const anexo1Dispositivos = computed(() => {
  const c = (cat.value.anexo1 || []).find(x => x.categoria === p.value.categoria_implantable)
  return c ? c.dispositivos : []
})
onMounted(async () => {
  try { cat.value = (await apiClient.get('/dm/catalogos')).data } catch (e) { console.warn('Catálogos DM no disponibles:', e?.message) }
})

// Defaults si el objeto viene sin campos DM (productos antiguos)
watch(() => props.modelValue, (obj) => {
  if (!obj) return
  for (const [k, v] of Object.entries(DM_DEFAULT_FIELDS)) if (obj[k] === undefined) obj[k] = v
  if (!obj.politica_vencimiento) obj.politica_vencimiento = 'fabricante'
}, { immediate: true })
// Un DM priorizado siempre es DM; si deja de ser implantable se limpian los campos del Anexo 1
watch(() => p.value.es_dm_priorizado, (v) => { if (v) p.value.es_dispositivo_medico = true })
watch(() => p.value.tipo_dm, (v) => {
  if (v !== 'implantable') { p.value.categoria_implantable = null; p.value.dispositivo_anexo1 = null }
})
</script>

<style scoped>
.dm-card { border-color: #26a69a !important; background: #f4fbfa; }
</style>
